import React, { useState, useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { AttachmentContext } from '@constants/attachments';
import { useAttachmentGallery } from './hooks/useAttachmentGallery';
import {
  DISPLAY_MODES,
  getDisplayMode,
  isCarouselMode,
  isGridMode
} from './utils/displayModes';
import { getCarouselOptions } from './utils/carouselConfig';
import GridDisplay from './components/GridDisplay';
import CarouselDisplay from './components/CarouselDisplay';
import FullscreenImagePreview from './components/FullscreenImagePreview';
import './AttachmentGallery.scss';

const AttachmentGallery = ({
  entityType,
  entityId,
  context,
  editable = false,
  onAttachmentDelete,
  onAttachmentUpdate,
  className = "",
  maxItems = null,
  showTitle = true,
  showInfo = true,
  thumbnailSize = null, // 80, 120, 300, or null for full size

  // New carousel props
  displayMode = DISPLAY_MODES.GRID, // 'grid' | 'carousel' | 'auto'
  carouselOptions = {},
  showCarouselControls = true,
  showCarouselDots = true,
  responsiveBreakpoints = null
}) => {
  const { t } = useLanguage();
  const [currentDisplayMode, setCurrentDisplayMode] = useState(displayMode);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const {
    attachments,
    loading,
    error,
    refreshKey,
    handleDelete,
    handleOrderUpdate,
    handleEditComplete
  } = useAttachmentGallery({
    entityType,
    entityId,
    context,
    maxItems,
    thumbnailSize,
    onAttachmentDelete,
    onAttachmentUpdate
  });

  // Handle responsive display mode changes
  useEffect(() => {
    const updateDisplayMode = () => {
      const resolvedMode = getDisplayMode(displayMode, responsiveBreakpoints);
      setCurrentDisplayMode(resolvedMode);
    };

    updateDisplayMode();

    if (displayMode === DISPLAY_MODES.AUTO) {
      window.addEventListener('resize', updateDisplayMode);
      return () => window.removeEventListener('resize', updateDisplayMode);
    }
  }, [displayMode, responsiveBreakpoints]);

  const getTitle = () => {
    if (!showTitle) return null;

    if (context === AttachmentContext.START_BORROWING) {
      return t('attachments.startPhotos');
    } else if (context === AttachmentContext.RETURN_BORROWING) {
      return t('attachments.returnPhotos');
    } else {
      return t('attachments.attachments');
    }
  };

  const getDisplayModeClassName = () => {
    const modeClass = `display-mode-${currentDisplayMode}`;
    if (context === AttachmentContext.START_BORROWING) {
      return `${modeClass} borrowing-gallery`;
    } else if (context === AttachmentContext.RETURN_BORROWING) {
      return `${modeClass} borrowing-gallery`;
    } else if (context === 'category') {
      return `${modeClass} category-gallery`;
    } else if (context === 'resource') {
      return `${modeClass} resource-gallery`;
    }
    return modeClass;
  };

  // Prepare attachments data for fullscreen preview
  const getFullscreenAttachments = () => {
    return attachments
      .filter(attachment => attachment.file?.mime_type?.startsWith('image/'))
      .map(attachment => ({
        id: attachment.id,
        file: attachment.file,
        fileUrl: `/api/attachments/${attachment.id}/file?t=${refreshKey}`,
        thumbnailUrl: thumbnailSize ? `/api/attachments/${attachment.id}/thumbnail/${thumbnailSize}?t=${refreshKey}` : null
      }));
  };

  // Handle fullscreen preview
  const handleImageClick = (attachmentId) => {
    const imageAttachments = getFullscreenAttachments();
    const index = imageAttachments.findIndex(att => att.id === attachmentId);
    if (index !== -1) {
      setFullscreenIndex(index);
      setShowFullscreenPreview(true);
    }
  };

  const handleCloseFullscreen = () => {
    setShowFullscreenPreview(false);
  };

  const handleFullscreenIndexChange = (newIndex) => {
    setFullscreenIndex(newIndex);
  };

  if (loading) {
    return (
      <div className={`attachment-gallery ${className} ${getDisplayModeClassName()}`}>
        {showTitle && <h3>{getTitle()}</h3>}
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>{t('attachments.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`attachment-gallery ${className} ${getDisplayModeClassName()}`}>
        {showTitle && <h3>{getTitle()}</h3>}
        <div className="error-container">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className={`attachment-gallery ${className} ${getDisplayModeClassName()}`}>
        {showTitle && <h3>{getTitle()}</h3>}
        <div className="empty-state">
          <div className="empty-placeholder">
          </div>
        </div>
      </div>
    );
  }

  const commonProps = {
    attachments,
    editable,
    onDelete: handleDelete,
    onOrderUpdate: handleOrderUpdate,
    onEditComplete: handleEditComplete,
    showInfo,
    thumbnailSize,
    refreshKey,
    onImageClick: handleImageClick
  };

  return (
    <div className={`attachment-gallery ${className} ${getDisplayModeClassName()}`}>
      {showTitle && <h3>{getTitle()}</h3>}

      {isCarouselMode(currentDisplayMode) ? (
        <CarouselDisplay
          {...commonProps}
          carouselOptions={getCarouselOptions(context, carouselOptions)}
          showControls={showCarouselControls}
          showDots={showCarouselDots}
          className={getDisplayModeClassName()}
        />
      ) : (
        <GridDisplay
          {...commonProps}
          className={getDisplayModeClassName()}
        />
      )}

      {/* Fullscreen Image Preview */}
      {showFullscreenPreview && (
        <FullscreenImagePreview
          attachments={getFullscreenAttachments()}
          currentIndex={fullscreenIndex}
          onClose={handleCloseFullscreen}
          onIndexChange={handleFullscreenIndexChange}
        />
      )}
    </div>
  );
};

export default AttachmentGallery;